'use client';

import { isPortalBuild } from '../lib/appTarget';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import { isQuotaExceededError } from '../lib/api/quotaError';
import { cn } from '../lib/cn';
import { Spinner } from './loading/Spinner';
import { Select, type SelectOption } from './Select';
import { createProject, createTeam, deleteTeam, fetchTeamMembers, type Project } from '../lib/api';
import { getAuthSession } from '../lib/authSession';
import { getDesktopPushEndpoint, hasDesktopBridge, openProjectInDesktop } from '../lib/desktopBridge';
import { canManageTeamMembers } from '../lib/permissions';
import { useProjectLandingNavigation } from '../hooks/useProjectLandingNavigation';
import { clearWorkspaceContext, getWorkspaceContext, saveWorkspaceContext } from '../lib/workspaceContext';
import { buildProjectLandingWorkspaceHref } from './layout/workspaceRouting';
import { resolveAppHref } from '../lib/portalRoutes';
import {
  useArchivedProjects,
  useLeaveTeamMutation,
  useProjects,
  useTeams,
  useUpdateTeamMutation,
} from '../hooks/queries/useTeams';
import { queryKeys } from '../hooks/queries/queryKeys';
import { useProjectsSubscriptionStatus } from '../hooks/queries/usePushNotifications';
import {
  usePreviewTeamSubscriptionSeatsMutation,
  useTeamSubscription,
  useUpdateTeamSubscriptionSeatsMutation,
} from '../hooks/queries/useBilling';
import { formatLocalDate } from '../lib/dateTime';
import { useUIStore } from '../stores/uiStore';
import TeamMembersView from './TeamMembersView';
import ProjectMembersView from './ProjectMembersView';
import ProjectRepositoriesView from './ProjectRepositoriesView';
import ProjectSettingsView from './ProjectSettingsView';
import ProjectTagsView from './ProjectTagsView';
import NotificationSettingsView from './NotificationSettingsView';
import { ProjectModal } from './ProjectModal';
import { BaseModal } from './BaseModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { TierBadge } from './billing/TierBadge';

const formatSeatPreviewAmount = (amount: number, currency: 'KRW' | 'USD') => {
  if (currency === 'KRW') return `₩${amount.toLocaleString()}`;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
};

export type ModalView =
  | { type: 'workspace' }
  | { type: 'team-members'; teamId: string; teamName: string }
  | { type: 'project-settings'; projectId: string; projectName: string }
  | { type: 'project-tags'; projectId: string; projectName: string }
  | { type: 'project-members'; projectId: string; projectName: string }
  | { type: 'project-repositories'; projectId: string; projectName: string }
  | { type: 'notification-settings'; projectId: string; projectName: string };

type WorkspaceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialView?: ModalView;
};

type WorkspaceTab = 'info' | 'members' | 'projects';

type WorkspaceAction =
  | { type: 'rename-team'; teamId: string; teamName: string }
  | { type: 'leave-team'; teamId: string; teamName: string };

function WorkspaceTeamsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="skeleton h-11 w-full rounded-xl" />
      <div className="skeleton h-24 w-full rounded-2xl" />
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={`workspace-project-skeleton-${index}`} className="skeleton h-16 w-full rounded-xl" />
      ))}
    </div>
  );
}

function WorkspaceProjectsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={`workspace-project-loading-${index}`} className="skeleton h-16 w-full rounded-xl" />
      ))}
    </div>
  );
}

const workspaceTabs = ['info', 'members', 'projects'] as const satisfies readonly WorkspaceTab[];

export default function WorkspaceModal({ isOpen, onClose, initialView }: WorkspaceModalProps) {
  const t = useTranslations('workspace.page');
  const tCommon = useTranslations('common');
  const tSettings = useTranslations('projects.settings');
  const tProjects = useTranslations('projects.members');
  const tRoot = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const session = useMemo(() => getAuthSession()!, []);
  const projectLanding = useProjectLandingNavigation(session.token);

  const [view, setView] = useState<ModalView>({ type: 'workspace' });
  const [projectModalState, setProjectModalState] = useState<{ projectId: string; projectName: string } | null>(null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<WorkspaceTab>('info');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectVisibility, setNewProjectVisibility] = useState<Project['visibility']>('PRIVATE');
  const [isSavingTeam, setIsSavingTeam] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState<string | null>(null);
  // 열기 진행 중인 프로젝트. 클릭이 왕복을 기다리는 동안 버튼이 먹통처럼 보이지 않게 한다.
  const [openingProjectId, setOpeningProjectId] = useState<string | null>(null);
  const latestOpenRequestRef = useRef(0);
  const [confirmDeleteTeamOpen, setConfirmDeleteTeamOpen] = useState(false);
  const [teamDeleteConfirmName, setTeamDeleteConfirmName] = useState('');
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [pushEndpoint, setPushEndpoint] = useState<string | null>(null);
  const [seatDraft, setSeatDraft] = useState(1);
  const [seatPreviewOpen, setSeatPreviewOpen] = useState(false);
  const [isBillingSectionOpen, setIsBillingSectionOpen] = useState(false);
  const [isDangerSectionOpen, setIsDangerSectionOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<WorkspaceAction | null>(null);
  const [actionNameDraft, setActionNameDraft] = useState('');
  const [actionConfirmDraft, setActionConfirmDraft] = useState('');
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (hasDesktopBridge()) {
      setPushEndpoint(getDesktopPushEndpoint());
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushEndpoint(null);
      return;
    }

    const syncEndpoint = async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushEndpoint(subscription?.endpoint ?? null);
    };

    void syncEndpoint().catch(() => {
      setPushEndpoint(null);
    });
  }, []);

  const { data: teams = [], isLoading: isLoadingTeams } = useTeams(session?.token ?? null);
  const { data: activeProjects = [], isLoading: isLoadingProjects } = useProjects(
    session?.token ?? null,
    selectedTeamId,
    isPortalBuild ? 'documents.read' : undefined,
  );
  const { data: archivedProjects = [], isLoading: isLoadingArchivedProjects } = useArchivedProjects(
    session?.token ?? null,
    selectedTeamId,
    showArchivedProjects && !isPortalBuild,
  );
  const projects = showArchivedProjects && !isPortalBuild ? archivedProjects : activeProjects;
  const isLoadingDisplayedProjects =
    showArchivedProjects && !isPortalBuild ? isLoadingArchivedProjects : isLoadingProjects;
  const { data: subscriptionStatuses = [] } = useProjectsSubscriptionStatus(session?.token ?? null, pushEndpoint);
  const { data: selectedTeamSubscription, isLoading: isLoadingSelectedTeamSubscription } = useTeamSubscription(
    session?.token ?? null,
    selectedTeamId || null,
  );
  const seatPreviewMutation = usePreviewTeamSubscriptionSeatsMutation(session?.token ?? null);
  const updateSeatsMutation = useUpdateTeamSubscriptionSeatsMutation(session?.token ?? null);
  const updateTeamMutation = useUpdateTeamMutation(session?.token ?? null);
  const leaveTeamMutation = useLeaveTeamMutation(session?.token ?? null);

  const subscriptionStatusMap = useMemo(() => {
    return new Map(subscriptionStatuses.map((row) => [row.projectId, row.isSubscribed] as const));
  }, [subscriptionStatuses]);
  const {
    data: selectedTeamMembers = [],
    isLoading: isLoadingSelectedTeamMembers,
    isFetching: isFetchingSelectedTeamMembers,
  } = useQuery({
    queryKey: queryKeys.teams.members(selectedTeamId),
    queryFn: () => fetchTeamMembers(session.token, selectedTeamId),
    enabled: selectedTeamId.length > 0,
  });

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [view, activeWorkspaceTab]);

  useEffect(() => {
    if (teams.length > 0 && !selectedTeamId) {
      const saved = getWorkspaceContext();
      const defaultTeam = saved && teams.some((team) => team.id === saved.teamId) ? saved.teamId : teams[0].id;
      setSelectedTeamId(defaultTeam);
    }
  }, [teams, selectedTeamId]);

  useEffect(() => {
    if (isOpen) {
      if (initialView?.type === 'team-members') {
        setView({ type: 'workspace' });
        setSelectedTeamId(initialView.teamId);
        setActiveWorkspaceTab('members');
        return;
      }

      setView(initialView ?? { type: 'workspace' });
      setActiveWorkspaceTab('info');
    }
  }, [isOpen, initialView]);

  const selectedTeam = teams.find((team) => team.id === selectedTeamId) ?? null;
  const projectCount = activeProjects.filter((project) => project.teamId === selectedTeamId).length;
  const displayedProjectCount = projects.filter((project) => project.teamId === selectedTeamId).length;
  const selectedTeamMembership = selectedTeamMembers.find((member) => member.memberId === session.member.id);
  const selectedTeamSeatCount = selectedTeamSubscription?.seats ?? Math.max(1, selectedTeamMembers.length);
  const isSelectedTeamMembershipLoading =
    selectedTeamId.length > 0 && (isLoadingSelectedTeamMembers || isFetchingSelectedTeamMembers);
  const isSelectedTeamAdmin = canManageTeamMembers(selectedTeamMembership?.role);
  const isSelectedTeamOwner = selectedTeamMembership?.role === 'OWNER';
  const isSelectedTeamPro = selectedTeamSubscription
    ? selectedTeamSubscription.status === 'ACTIVE' || selectedTeamSubscription.status === 'TRIALING'
    : false;
  const isTeamDeleteNameMatched = selectedTeam ? teamDeleteConfirmName.trim() === selectedTeam.name : false;

  useEffect(() => {
    setTeamDeleteConfirmName('');
    setSeatPreviewOpen(false);
    setShowArchivedProjects(false);
  }, [selectedTeamId]);

  useEffect(() => {
    setSeatDraft(selectedTeamSeatCount);
  }, [selectedTeamSeatCount]);

  useEffect(() => {
    if (!activeAction) {
      setActionNameDraft('');
      setActionConfirmDraft('');
      return;
    }

    if (activeAction.type === 'rename-team') {
      setActionNameDraft(activeAction.teamName);
    }
  }, [activeAction]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setIsSavingTeam(true);
    try {
      const created = await createTeam(session.token, { name: newTeamName.trim() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      setSelectedTeamId(created.id);
      setNewTeamName('');
      setShowCreateTeamModal(false);
    } catch (saveError) {
      if (isQuotaExceededError(saveError)) {
        toast.error(t('teamQuotaExceeded'));
        return;
      }

      const message = resolveApiErrorToastMessage(tRoot, saveError);
      if (message) toast.error(message);
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleOpenProjectDashboard = async (
    project: { id: string; name: string },
    team: { id: string; name: string },
  ) => {
    // 연속 클릭 대비 토큰. 늦게 도착한 응답으로 이동하면 저장 컨텍스트와 실제 화면이 어긋나
    // 다음 진입에서 프로젝트가 뒤바뀐다.
    const requestId = ++latestOpenRequestRef.current;
    setOpeningProjectId(project.id);

    // 판정이 끝난 뒤에 목적지를 정한다 — 먼저 이동하면 권한 없는 화면으로 보내게 된다.
    // 데스크탑 브릿지도 같은 경로를 받아야 한다(웹만 고치면 데스크탑에서만 접근 거부가 남는다).
    const landingPath = isPortalBuild ? '/documents' : await projectLanding.resolveLandingPath(project.id);
    if (latestOpenRequestRef.current !== requestId) return;
    setOpeningProjectId(null);

    // 컨텍스트 저장은 실제로 이동하는 클릭에서만 한다 — 목적지와 저장 값이 항상 같은 클릭에서 나온다.
    saveWorkspaceContext({
      teamId: team.id,
      teamName: team.name,
      projectId: project.id,
      projectName: project.name,
    });

    if (isPortalBuild) {
      onClose();
      router.push(buildProjectLandingWorkspaceHref('/documents', team.id, project.id));
      return;
    }

    const openedInDesktop = openProjectInDesktop(
      {
        projectId: project.id,
        teamId: team.id,
        name: project.name,
        teamName: team.name,
      },
      landingPath,
    );

    onClose();
    if (openedInDesktop) return;

    router.push(resolveAppHref(buildProjectLandingWorkspaceHref(landingPath, team.id, project.id)));
  };

  const handlePreviewSeats = async () => {
    if (!selectedTeamId) return;
    try {
      await seatPreviewMutation.mutateAsync({ teamId: selectedTeamId, seats: seatDraft });
      setSeatPreviewOpen(true);
    } catch (previewError) {
      const message = resolveApiErrorToastMessage(tRoot, previewError);
      if (message) toast.error(message);
    }
  };

  const handleConfirmSeatChange = async () => {
    if (!selectedTeamId) return;
    try {
      await updateSeatsMutation.mutateAsync({ teamId: selectedTeamId, seats: seatDraft });
      setSeatPreviewOpen(false);
      toast.success(t('billingSeatsUpdated'));
    } catch (updateError) {
      const message = resolveApiErrorToastMessage(tRoot, updateError);
      if (message) toast.error(message);
    }
  };

  const handleDeleteSelectedTeam = async () => {
    if (!selectedTeam) return;
    setConfirmDeleteTeamOpen(false);
    setDeletingTeamId(selectedTeam.id);
    try {
      await deleteTeam(session.token, selectedTeam.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      queryClient.removeQueries({ queryKey: queryKeys.projects.byTeam(selectedTeam.id) });
      queryClient.removeQueries({ queryKey: queryKeys.teams.members(selectedTeam.id) });
      setSelectedTeamId('');
      setTeamDeleteConfirmName('');
    } catch (deleteError) {
      const message = resolveApiErrorToastMessage(tRoot, deleteError);
      if (message) toast.error(message);
    } finally {
      setDeletingTeamId(null);
    }
  };

  const handleRequestDeleteSelectedTeam = () => {
    if (!selectedTeam) return;
    setConfirmDeleteTeamOpen(true);
  };

  const handleOpenTeamBilling = () => {
    if (!selectedTeam || !isSelectedTeamOwner) return;
    onClose();
    useUIStore.getState().openSubscribeModal({
      mode: 'team',
      teamId: selectedTeam.id,
      teamName: selectedTeam.name,
      defaultSeats: selectedTeamSeatCount,
    });
  };

  const closeActionModal = () => {
    if (updateTeamMutation.isPending || leaveTeamMutation.isPending) {
      return;
    }
    setActiveAction(null);
  };

  const handleSubmitAction = async () => {
    if (!activeAction) return;

    try {
      if (activeAction.type === 'rename-team') {
        const nextName = actionNameDraft.trim();
        if (!nextName || nextName === activeAction.teamName) return;
        await updateTeamMutation.mutateAsync({
          teamId: activeAction.teamId,
          input: { name: nextName },
        });
        toast.success(t('teamRenamed'));
      } else if (activeAction.type === 'leave-team') {
        await leaveTeamMutation.mutateAsync({ teamId: activeAction.teamId });
        const saved = getWorkspaceContext();
        if (saved?.teamId === activeAction.teamId) {
          clearWorkspaceContext();
        }
        setSelectedTeamId('');
        toast.success(t('teamLeft'));
      }
      setActiveAction(null);
    } catch (actionError) {
      const message = resolveApiErrorToastMessage(tRoot, actionError);
      if (message) toast.error(message);
    }
  };

  const headerTitle =
    view.type === 'workspace'
      ? t('title')
      : view.type === 'project-settings'
        ? t('projectSettings')
        : view.type === 'project-tags'
          ? t('projectTags')
          : view.type === 'project-members'
            ? tProjects('title')
            : view.type === 'project-repositories'
              ? t('repositories')
              : t('notificationSettings');

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        variant="bottomSheet"
        size="3xl"
        height="h-[85vh] max-h-[620px] sm:h-[70vh]"
        className="!p-0 flex flex-col overflow-hidden bg-base-100"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-base-300 bg-base-100/95 px-5 py-3 backdrop-blur">
          <div className="flex items-center gap-1.5">
            {view.type !== 'workspace' ? (
              <button
                type="button"
                aria-label="Back"
                className="btn btn-ghost btn-circle btn-sm"
                onClick={() => setView({ type: 'workspace' })}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            ) : null}
            <div>
              <h3 className="text-base font-semibold leading-tight">{headerTitle}</h3>
              {view.type === 'project-settings' ||
              view.type === 'project-tags' ||
              view.type === 'project-members' ||
              view.type === 'project-repositories' ||
              view.type === 'notification-settings' ? (
                <p className="text-xs text-[var(--color-fg-subtle)]">{view.projectName}</p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {view.type === 'workspace' ? (
              isLoadingTeams ? (
                <span className="skeleton h-8 w-24 rounded-lg" />
              ) : teams.length === 0 ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm gap-1.5"
                  onClick={() => setShowCreateTeamModal(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('createTeam')}
                </button>
              ) : (
                <div className="dropdown dropdown-end">
                  <div
                    tabIndex={0}
                    role="button"
                    className="btn btn-ghost btn-sm gap-1.5 border border-base-300 normal-case"
                  >
                    <span className="hidden text-xs text-[var(--color-fg-subtle)] sm:inline">{t('manageTeam')}</span>
                    <span>{selectedTeam?.name || t('selectTeam')}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5 opacity-50"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  <ul
                    tabIndex={0}
                    className="menu dropdown-content z-[50] mt-2 w-64 rounded-xl border border-base-300 bg-base-100 p-2 shadow-lg max-h-72 overflow-y-auto"
                  >
                    {teams.map((team) => (
                      <li key={team.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTeamId(team.id);
                            (document.activeElement as HTMLElement | null)?.blur();
                          }}
                          className={cn(team.id === selectedTeamId && 'bg-primary/10 text-primary')}
                        >
                          {team.name}
                        </button>
                      </li>
                    ))}
                    <div className="divider my-1" />
                    <li>
                      <button type="button" onClick={() => setShowCreateTeamModal(true)} className="gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        {t('createTeam')}
                      </button>
                    </li>
                  </ul>
                </div>
              )
            ) : null}
            <button
              type="button"
              aria-label={tCommon('actions.close')}
              className="btn btn-ghost btn-circle btn-sm"
              onClick={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-5">
          {view.type === 'project-settings' ? (
            <ProjectSettingsView projectId={view.projectId} />
          ) : view.type === 'project-tags' ? (
            <ProjectTagsView projectId={view.projectId} />
          ) : view.type === 'project-members' ? (
            <ProjectMembersView projectId={view.projectId} />
          ) : view.type === 'project-repositories' ? (
            <ProjectRepositoriesView projectId={view.projectId} />
          ) : view.type === 'notification-settings' ? (
            <NotificationSettingsView
              projectId={view.projectId}
              isSubscribed={subscriptionStatusMap.get(view.projectId) ?? false}
              pushEndpoint={pushEndpoint}
              onPushEndpointChangeAction={(endpoint) => setPushEndpoint(endpoint)}
            />
          ) : isLoadingTeams ? (
            <WorkspaceTeamsSkeleton />
          ) : (
            <>
              {teams.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-base-300 py-16">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                    className="mb-4 h-12 w-12 text-[var(--color-fg-subtle)]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <h3 className="text-lg font-bold text-base-content">{t('emptyTeamTitle')}</h3>
                  <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{t('emptyTeamDescription')}</p>
                </div>
              ) : !selectedTeam ? null : (
                <>
                  <div className="mb-4 flex flex-col gap-3 border-b border-base-300 pb-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
                        {t('manageTeam')}
                      </p>
                      <h4 className="mt-1 truncate text-lg font-semibold">{selectedTeam.name}</h4>
                      <p className="mt-1 text-xs text-[var(--color-fg-subtle)]">
                        {t('tabSummary', {
                          members: selectedTeamMembers.length,
                          projects: projectCount,
                        })}
                      </p>
                    </div>
                    <div
                      role="tablist"
                      aria-label={t('workspaceTabs')}
                      className="grid w-full grid-cols-3 rounded-xl bg-base-200 p-1 sm:w-auto sm:min-w-[21rem]"
                    >
                      {workspaceTabs.map((tab) => (
                        <button
                          key={tab}
                          id={`workspace-tab-${tab}`}
                          type="button"
                          role="tab"
                          aria-selected={activeWorkspaceTab === tab}
                          aria-controls={`workspace-tabpanel-${tab}`}
                          className={cn(
                            'btn btn-ghost btn-sm min-h-9 whitespace-nowrap rounded-lg border-0 px-1 text-xs font-medium sm:px-3 sm:text-sm',
                            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                            activeWorkspaceTab === tab && 'bg-base-100 text-primary shadow-xs',
                          )}
                          onClick={() => setActiveWorkspaceTab(tab)}
                        >
                          {t(`tabs.${tab}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {activeWorkspaceTab === 'info' ? (
                    <div id="workspace-tabpanel-info" role="tabpanel" aria-labelledby="workspace-tab-info">
                      <section className="mb-4 rounded-xl border border-base-300 bg-base-100 p-4 shadow-xs">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
                              {t('teamSettings')}
                            </p>
                            <h4 className="mt-1 truncate text-base font-semibold">{selectedTeam.name}</h4>
                            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
                              {isSelectedTeamAdmin ? t('teamSettingsDescription') : t('teamMemberDescription')}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {isSelectedTeamAdmin ? (
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() =>
                                  setActiveAction({
                                    type: 'rename-team',
                                    teamId: selectedTeam.id,
                                    teamName: selectedTeam.name,
                                  })
                                }
                              >
                                {t('renameTeam')}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-outline btn-error btn-sm"
                              onClick={() =>
                                setActiveAction({
                                  type: 'leave-team',
                                  teamId: selectedTeam.id,
                                  teamName: selectedTeam.name,
                                })
                              }
                            >
                              {t('leaveTeam')}
                            </button>
                          </div>
                        </div>
                      </section>

                      <section className="mb-4 rounded-xl border border-base-300 bg-base-100 shadow-xs">
                        <button
                          type="button"
                          className="flex w-full items-start gap-3 p-4 text-left"
                          aria-expanded={isBillingSectionOpen}
                          aria-controls={`workspace-billing-section-${selectedTeam.id}`}
                          onClick={() => setIsBillingSectionOpen((value) => !value)}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="text-sm font-semibold">{t('billingTitle')}</h4>
                              <TierBadge
                                tier={isSelectedTeamPro ? 'PRO' : 'FREE'}
                                subscriptionStatus={selectedTeamSubscription?.status ?? null}
                              />
                            </div>
                            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('billingDescription')}</p>
                          </div>
                          <span className="btn btn-ghost btn-circle btn-xs shrink-0" aria-hidden>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={cn('h-4 w-4 transition-transform', isBillingSectionOpen && 'rotate-180')}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </button>
                        {isBillingSectionOpen ? (
                          <div
                            id={`workspace-billing-section-${selectedTeam.id}`}
                            className="border-t border-base-300 px-4 pb-4 pt-3"
                          >
                            <div className="grid gap-2 text-xs text-[var(--color-fg-muted)] sm:grid-cols-3">
                              <div className="rounded-lg bg-base-200/60 px-3 py-2">
                                <span className="block text-[var(--color-fg-subtle)]">{t('billingSeats')}</span>
                                <span className="font-semibold">
                                  {isLoadingSelectedTeamSubscription ? '–' : selectedTeamSeatCount}
                                </span>
                              </div>
                              <div className="rounded-lg bg-base-200/60 px-3 py-2">
                                <span className="block text-[var(--color-fg-subtle)]">{t('billingRenewal')}</span>
                                <span className="font-semibold">
                                  {selectedTeamSubscription?.currentPeriodEndAt
                                    ? formatLocalDate(selectedTeamSubscription.currentPeriodEndAt)
                                    : '–'}
                                </span>
                              </div>
                              <div className="rounded-lg bg-base-200/60 px-3 py-2">
                                <span className="block text-[var(--color-fg-subtle)]">{t('billingOwnerOnly')}</span>
                                <span className="font-semibold">{isSelectedTeamOwner ? t('yes') : t('no')}</span>
                              </div>
                            </div>
                            {isSelectedTeamOwner ? (
                              <div className="mt-3">
                                {isSelectedTeamPro ? (
                                  <div className="border-t border-base-300 pt-3">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                                      <div className="form-control flex-1">
                                        <label
                                          className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                                          htmlFor="wm-team-seats"
                                        >
                                          {t('billingSeats')}
                                        </label>
                                        <input
                                          id="wm-team-seats"
                                          type="number"
                                          min={Math.max(1, selectedTeamMembers.length)}
                                          className="input input-bordered input-sm w-full"
                                          value={seatDraft}
                                          onChange={(event) =>
                                            setSeatDraft(
                                              Math.max(
                                                Math.max(1, selectedTeamMembers.length),
                                                Number(event.target.value) || 1,
                                              ),
                                            )
                                          }
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        disabled={
                                          seatDraft === selectedTeamSeatCount ||
                                          seatPreviewMutation.isPending ||
                                          updateSeatsMutation.isPending
                                        }
                                        onClick={() => void handlePreviewSeats()}
                                      >
                                        {seatPreviewMutation.isPending ? <Spinner /> : t('billingPreviewSeats')}
                                      </button>
                                    </div>
                                    {seatPreviewOpen && seatPreviewMutation.data ? (
                                      <div className="mt-3 border-t border-base-300 pt-3 text-xs text-[var(--color-fg-muted)]">
                                        <p className="font-medium text-base-content">
                                          {t('billingSeatPreview', {
                                            current: seatPreviewMutation.data.currentSeats,
                                            next: seatPreviewMutation.data.newSeats,
                                            amount: formatSeatPreviewAmount(
                                              Math.max(0, seatPreviewMutation.data.proratedAmount),
                                              seatPreviewMutation.data.currency,
                                            ),
                                          })}
                                        </p>
                                        <p className="mt-1">
                                          {t('billingSeatPreviewMeta', {
                                            days: seatPreviewMutation.data.remainingDays,
                                            mode: t(`billingProrationMode.${seatPreviewMutation.data.prorationMode}`),
                                          })}
                                        </p>
                                        <div className="mt-2 flex justify-end gap-2">
                                          <button
                                            type="button"
                                            className="btn btn-ghost btn-xs"
                                            onClick={() => setSeatPreviewOpen(false)}
                                          >
                                            {tCommon('actions.cancel')}
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-primary btn-xs"
                                            disabled={updateSeatsMutation.isPending}
                                            onClick={() => void handleConfirmSeatChange()}
                                          >
                                            {updateSeatsMutation.isPending ? <Spinner /> : t('billingConfirmSeats')}
                                          </button>
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={handleOpenTeamBilling}
                                    >
                                      {t('billingUpgrade')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </section>

                      {isSelectedTeamOwner ? (
                        <section className="rounded-xl border border-error/30 bg-error/5 shadow-xs">
                          <button
                            type="button"
                            className="flex w-full items-start gap-3 p-4 text-left"
                            aria-expanded={isDangerSectionOpen}
                            aria-controls={`workspace-danger-section-${selectedTeam.id}`}
                            onClick={() => setIsDangerSectionOpen((value) => !value)}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-error/80">
                                {t('dangerZone')}
                              </p>
                              <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('dangerDescription')}</p>
                            </div>
                            <span className="btn btn-ghost btn-circle btn-xs shrink-0" aria-hidden>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={cn('h-4 w-4 transition-transform', isDangerSectionOpen && 'rotate-180')}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </span>
                          </button>
                          {isDangerSectionOpen ? (
                            <div
                              id={`workspace-danger-section-${selectedTeam.id}`}
                              className="border-t border-error/20 px-4 pb-4 pt-3"
                            >
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <input
                                  type="text"
                                  className="input input-bordered input-sm w-full sm:max-w-xs"
                                  placeholder={selectedTeam?.name ?? t('teamName')}
                                  value={teamDeleteConfirmName}
                                  onChange={(event) => setTeamDeleteConfirmName(event.target.value)}
                                />
                                <button
                                  type="button"
                                  className="btn btn-error btn-sm gap-1.5"
                                  disabled={deletingTeamId === selectedTeam.id || !isTeamDeleteNameMatched}
                                  onClick={() => void handleRequestDeleteSelectedTeam()}
                                >
                                  {deletingTeamId === selectedTeam.id ? (
                                    <>
                                      <Spinner />
                                      {t('deleting')}
                                    </>
                                  ) : (
                                    t('deleteTeam')
                                  )}
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </section>
                      ) : null}
                    </div>
                  ) : activeWorkspaceTab === 'members' ? (
                    <div id="workspace-tabpanel-members" role="tabpanel" aria-labelledby="workspace-tab-members">
                      <TeamMembersView teamId={selectedTeam.id} />
                    </div>
                  ) : activeWorkspaceTab === 'projects' ? (
                    <div id="workspace-tabpanel-projects" role="tabpanel" aria-labelledby="workspace-tab-projects">
                      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-4 shadow-xs md:flex-row md:items-end">
                        <div className="form-control flex-1">
                          <label
                            className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                            htmlFor="wm-new-project-name"
                          >
                            {t('newProject')}
                          </label>
                          <input
                            id="wm-new-project-name"
                            className="input input-bordered input-sm w-full"
                            placeholder={t('newProjectPlaceholder')}
                            value={newProjectName}
                            onChange={(event) => setNewProjectName(event.target.value)}
                          />
                        </div>
                        <div className="form-control md:w-44">
                          <label
                            className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
                            htmlFor="wm-new-project-visibility"
                          >
                            {tSettings('visibilityLabel')}
                          </label>
                          <Select
                            id="wm-new-project-visibility"
                            options={
                              [
                                { value: 'TEAM', label: tSettings('visibilityTeam') },
                                { value: 'PRIVATE', label: tSettings('visibilityPrivate') },
                              ] satisfies SelectOption[]
                            }
                            value={newProjectVisibility}
                            onChange={(value) => setNewProjectVisibility(value as Project['visibility'])}
                            size="sm"
                            fullWidth
                            disabled={!isSelectedTeamAdmin || isSavingProject}
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm gap-1.5"
                          disabled={!isSelectedTeamAdmin || isSavingProject || !newProjectName.trim()}
                          onClick={async () => {
                            if (!selectedTeam) return;
                            setIsSavingProject(true);
                            try {
                              await createProject(session.token, selectedTeam.id, {
                                name: newProjectName.trim(),
                                visibility: newProjectVisibility,
                              });
                              await queryClient.invalidateQueries({
                                queryKey: queryKeys.projects.byTeam(selectedTeam.id),
                              });
                              await queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
                              setNewProjectName('');
                              setNewProjectVisibility('PRIVATE');
                            } catch (saveError) {
                              if (isQuotaExceededError(saveError)) {
                                toast.error(t('projectQuotaExceeded'));
                                return;
                              }

                              const message = resolveApiErrorToastMessage(tRoot, saveError);
                              if (message) toast.error(message);
                            } finally {
                              setIsSavingProject(false);
                            }
                          }}
                        >
                          {isSavingProject ? (
                            <Spinner />
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                          )}
                          {isSavingProject ? t('creating') : t('createProject')}
                        </button>
                      </div>
                      {!isSelectedTeamAdmin && !isSelectedTeamMembershipLoading ? (
                        <p className="-mt-2 mb-3 text-xs text-warning">{t('permissionHint')}</p>
                      ) : null}

                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-xs font-medium text-[var(--color-fg-subtle)]">
                          {showArchivedProjects ? t('archivedProjectsTitle') : t('activeProjectsTitle')}
                        </p>
                        <button
                          type="button"
                          className={cn(
                            'btn btn-ghost btn-xs gap-1.5',
                            showArchivedProjects && 'bg-base-200 text-primary',
                          )}
                          aria-pressed={showArchivedProjects}
                          onClick={() => setShowArchivedProjects((value) => !value)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M20 7H4l1.27-2.54A2 2 0 017.06 3.5h9.88a2 2 0 011.79 1.96L20 7zm0 0v11a2 2 0 01-2 2H6a2 2 0 01-2-2V7m6 5h4"
                            />
                          </svg>
                          {showArchivedProjects ? t('showActive') : t('showArchived')}
                        </button>
                      </div>

                      {isLoadingDisplayedProjects ? (
                        <WorkspaceProjectsSkeleton />
                      ) : displayedProjectCount === 0 ? (
                        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-base-300 py-12">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden
                            className="mb-4 h-10 w-10 text-[var(--color-fg-subtle)]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.5"
                              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                            />
                          </svg>
                          <h3 className="text-lg font-bold text-base-content">
                            {showArchivedProjects ? t('emptyArchivedProjectsTitle') : t('emptyProjectTitle')}
                          </h3>
                          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
                            {showArchivedProjects
                              ? t('emptyArchivedProjectsDescription')
                              : t('emptyProjectDescription')}
                          </p>
                        </div>
                      ) : (
                        <ul className="space-y-2">
                          {projects
                            .filter((project) => project.teamId === selectedTeam.id)
                            .map((project) => (
                              <li key={project.id}>
                                <div className="flex items-center justify-between gap-3 rounded-xl border border-base-300 bg-base-100 p-4 transition-colors hover:border-base-content/20 hover:bg-base-200">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setProjectModalState({ projectId: project.id, projectName: project.name })
                                    }
                                    className="group flex min-w-0 flex-1 items-center gap-3 text-left"
                                  >
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-base-200">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 text-[var(--color-fg-muted)]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="1.5"
                                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                                        />
                                      </svg>
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium truncate">{project.name}</span>
                                        {project.archivedAt ? (
                                          <span className="badge badge-ghost badge-sm shrink-0">
                                            {t('archivedBadge')}
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm shrink-0 gap-1.5"
                                    aria-label={t('openProjectDashboard', { name: project.name })}
                                    // 목적지 판정이 끝날 때까지 같은 버튼 재클릭을 막는다(다른 프로젝트로의
                                    // 전환은 허용하고, 늦게 온 응답은 핸들러의 최신 클릭 가드가 버린다).
                                    disabled={openingProjectId === project.id}
                                    onClick={() => void handleOpenProjectDashboard(project, selectedTeam)}
                                  >
                                    {openingProjectId === project.id ? (
                                      <Spinner size="xs" />
                                    ) : (
                                      <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                                    )}
                                    {t('openDashboard')}
                                  </button>
                                </div>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      </BaseModal>

      <BaseModal
        isOpen={showCreateTeamModal}
        onClose={() => {
          setShowCreateTeamModal(false);
          setNewTeamName('');
        }}
        variant="bottomSheet"
        size="md"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </span>
          <div>
            <h3 className="text-lg font-bold">{t('createTeamTitle')}</h3>
            <p className="text-sm text-[var(--color-fg-muted)]">{t('createTeamDescription')}</p>
          </div>
        </div>
        <div className="mt-6">
          <label className="mb-1.5 block text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="wm-new-team-name">
            {t('teamName')}
          </label>
          <input
            id="wm-new-team-name"
            className="input input-bordered w-full"
            placeholder={t('newTeamPlaceholder')}
            value={newTeamName}
            onChange={(event) => setNewTeamName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void handleCreateTeam();
            }}
          />
        </div>
        <div className="modal-action">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setShowCreateTeamModal(false);
              setNewTeamName('');
            }}
          >
            {tCommon('actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm gap-1.5"
            disabled={isSavingTeam || !newTeamName.trim()}
            onClick={() => void handleCreateTeam()}
          >
            {isSavingTeam ? (
              <>
                <Spinner />
                {t('creating')}
              </>
            ) : (
              t('create')
            )}
          </button>
        </div>
      </BaseModal>

      <BaseModal isOpen={activeAction !== null} onClose={closeActionModal} variant="bottomSheet" size="md">
        {activeAction ? (
          <div>
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                  activeAction.type === 'leave-team' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary',
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.7"
                    d="M12 6v6h4.5M4.5 12a7.5 7.5 0 1115 0 7.5 7.5 0 01-15 0z"
                  />
                </svg>
              </span>
              <div className="min-w-0">
                <h3 className="text-lg font-bold">
                  {activeAction.type === 'rename-team' ? t('renameTeam') : t('leaveTeam')}
                </h3>
                <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
                  {activeAction.type === 'rename-team'
                    ? t('renameTeamDescription')
                    : t('leaveTeamDescription', { name: activeAction.teamName })}
                </p>
              </div>
            </div>

            {activeAction.type === 'rename-team' ? (
              <div className="mt-6">
                <label
                  className="mb-1.5 block text-xs font-medium text-[var(--color-fg-subtle)]"
                  htmlFor="wm-action-name"
                >
                  {t('teamName')}
                </label>
                <input
                  id="wm-action-name"
                  className="input input-bordered w-full"
                  value={actionNameDraft}
                  onChange={(event) => setActionNameDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void handleSubmitAction();
                  }}
                />
              </div>
            ) : null}

            {activeAction.type === 'leave-team' ? (
              <div className="mt-6">
                <label
                  className="mb-1.5 block text-xs font-medium text-[var(--color-fg-muted)]"
                  htmlFor="wm-action-confirm"
                >
                  {t('confirmByTyping', { name: activeAction.teamName })}
                </label>
                <input
                  id="wm-action-confirm"
                  className="input input-bordered w-full"
                  value={actionConfirmDraft}
                  onChange={(event) => setActionConfirmDraft(event.target.value)}
                />
              </div>
            ) : null}

            <div className="modal-action">
              <button type="button" className="btn btn-ghost btn-sm" onClick={closeActionModal}>
                {tCommon('actions.cancel')}
              </button>
              <button
                type="button"
                className={cn('btn btn-sm', activeAction.type === 'leave-team' ? 'btn-warning' : 'btn-primary')}
                disabled={
                  (activeAction.type === 'rename-team' &&
                    (!actionNameDraft.trim() ||
                      actionNameDraft.trim() === activeAction.teamName ||
                      updateTeamMutation.isPending)) ||
                  (activeAction.type === 'leave-team' &&
                    (actionConfirmDraft !== activeAction.teamName || leaveTeamMutation.isPending))
                }
                onClick={() => void handleSubmitAction()}
              >
                {updateTeamMutation.isPending || leaveTeamMutation.isPending ? <Spinner /> : null}
                {activeAction.type === 'rename-team' ? tCommon('actions.save') : t('leave')}
              </button>
            </div>
          </div>
        ) : null}
      </BaseModal>

      <DeleteConfirmModal
        isOpen={confirmDeleteTeamOpen}
        title={tCommon('actions.delete')}
        message={t('confirmDeleteTeam', {
          name: selectedTeam && selectedTeam.name.trim().length > 0 ? selectedTeam.name : t('unnamedTeam'),
        })}
        confirmLabel={tCommon('actions.delete')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={deletingTeamId !== null}
        onConfirm={() => void handleDeleteSelectedTeam()}
        onCancel={() => {
          if (deletingTeamId === null) {
            setConfirmDeleteTeamOpen(false);
          }
        }}
      />

      {projectModalState ? (
        <ProjectModal
          isOpen
          onClose={() => setProjectModalState(null)}
          projectId={projectModalState.projectId}
          projectName={projectModalState.projectName}
          onProjectDeleted={() => setProjectModalState(null)}
        />
      ) : null}
    </>
  );
}
